import { useState } from 'react';
import { Flex, Box, IconButton, Button, Text, Heading, Icon, Label, Loader, TextField, Tooltip, Divider } from '@vibe/core';
import { AppModal, AppModalContent } from '@gorilla/ui/src/components/AppModal';
import { Board, Bullet, Delete, Duplicate } from '@vibe/icons';
import { Widget } from '../hooks/use-widgets';

type WidgetListProps = {
  widgets: Widget[] | undefined;
  boardName: string | undefined;
  isBusy: boolean;
  onOpen: (widget: Widget) => void;
  onCreate: (name: string) => Promise<unknown>;
  onDuplicate: (widget: Widget) => Promise<unknown>;
  onRename: (widget: Widget, name: string) => Promise<unknown>;
  onDelete: (widgetId: string) => Promise<unknown>;
};

// same colors the editor bar's publish indicator uses
const PUBLISHED_COLOR = '#00ca72';
const UNPUBLISHED_COLOR = '#d83a52';

// mirrors MAX_WIDGETS_PER_BOARD in widgets-backend, so the create actions go quiet before a user
// runs into the backend's 400. the backend stays the one that enforces it.
const MAX_WIDGETS_PER_BOARD = 5;
const LIMIT_REACHED_MESSAGE = `Maximum of ${MAX_WIDGETS_PER_BOARD} widgets reached`;

function defaultNameFor(widgets: Widget[], boardName: string | undefined) {
  if (!widgets.length) {
    return boardName || 'Widget';
  }

  return `Widget ${widgets.length + 1}`;
}

// widgets created before names existed have none - the board's default widget is the one those
// users already know, so fall back to the board name rather than "Untitled"
function displayName(widget: Widget, boardName: string | undefined) {
  return widget.name || (widget.isDefault ? boardName || 'Widget' : 'Untitled widget');
}

export function WidgetList({ widgets, boardName, isBusy, onOpen, onCreate, onDuplicate, onRename, onDelete }: WidgetListProps) {
  const [newName, setNewName] = useState<string | null>(null);
  const [widgetToRename, setWidgetToRename] = useState<Widget | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [widgetToDelete, setWidgetToDelete] = useState<Widget | null>(null);

  const isLimitReached = !!widgets && widgets.length >= MAX_WIDGETS_PER_BOARD;

  if (!widgets) {
    return (
      <Flex justify="center" align="center" style={{ height: '100vh' }}>
        <Loader size="medium" />
      </Flex>
    );
  }

  const newWidgetButton = isLimitReached ? (
    <Tooltip content={LIMIT_REACHED_MESSAGE} position="left" showOnDialogEnter={false} hideWhenReferenceHidden={true}>
      <Button kind="secondary" size="medium" disabled>
        New widget
      </Button>
    </Tooltip>
  ) : (
    <Button kind="primary" size="medium" disabled={isBusy || isLimitReached} onClick={() => setNewName(defaultNameFor(widgets, boardName))}>
      New widget
    </Button>
  );

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        minHeight: '100vh',
        backgroundColor: '#fbfcff',
      }}
    >
      <Box
        border
        rounded="small"
        backgroundColor="primaryBackgroundColor"
        shadow="xs"
        style={{
          maxWidth: '760px',
          margin: '64px auto',
        }}
      >
        <Box padding="large">
          <Flex justify="space-between" align="center">
            <Flex gap={8} align="center">
              <Icon icon={Board} size={24} style={{ transform: 'translate3d(0,-1px,0)' }} />
              <Heading type="h3" weight="bold" align="start">
                Widgets on this board
              </Heading>
            </Flex>
            {newWidgetButton}
          </Flex>
        </Box>

        <Divider withoutMargin />

        {widgets.length === 0 ? (
          <Box padding="xxl">
            <Flex gap={8} direction="column" align="center">
              <Text type="text1" align="center" weight="bold" color="primary">
                No widgets yet
              </Text>
              <Text type="text2" align="center" color="secondary">
                Create one to turn this board into a widget for your website.
              </Text>
            </Flex>
          </Box>
        ) : null}

        <Flex direction="column" gap={0} align="stretch">
          {widgets.map((widget, index) => (
            <Box
              key={widget.id}
              padding="large"
              paddingTop="medium"
              paddingBottom="medium"
              paddingStart="medium"
              style={{ borderTop: index > 0 ? '1px solid #d0d4e4' : 'none' }}
            >
              <Flex justify="space-between" align="center" gap={12}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <Flex gap={8} align="center">
                    <Flex gap={0} align="center">
                      <span style={{ display: 'inline-flex', color: widget.publishedAt ? PUBLISHED_COLOR : UNPUBLISHED_COLOR }}>
                        <Icon icon={Bullet} size={24} />
                      </span>
                      <div style={{ minWidth: 0 }}>
                        <Button kind="tertiary" size="small" onClick={() => onOpen(widget)}>
                          {displayName(widget, boardName)}
                        </Button>
                      </div>
                    </Flex>
                    {/* the default widget has no delete action - say why, so its missing button
                        reads as deliberate rather than broken */}
                    {widget.isDefault ? (
                      <Tooltip
                        content="This is the board's main widget. It keeps the board's original embed code and can't be deleted."
                        position="top"
                      >
                        <div style={{ cursor: 'default', flexShrink: 0 }}>
                          <Label text="Default" size="small" kind="line" color="dark" />
                        </div>
                      </Tooltip>
                    ) : null}
                  </Flex>
                </div>

                <Flex gap={8} align="center">
                  {/* the board's main widget owns the embed URL every other widget is derived
                      around, so it has no delete action at all - and a published widget has to be
                      unpublished from the editor first, so it can never vanish from a live site */}
                  <Flex gap={0} align="center">
                    {widget.isDefault ? null : widget.publishedAt ? (
                      <Tooltip content="Unpublish first to delete it" position="left">
                        <div>
                          <Button kind="tertiary" size="small" disabled aria-label="Delete widget">
                            <Icon icon={Delete} size={16} />
                          </Button>
                        </div>
                      </Tooltip>
                    ) : (
                      <IconButton
                        icon={Delete}
                        kind="tertiary"
                        size="small"
                        disabled={isBusy}
                        onClick={() => setWidgetToDelete(widget)}
                        tooltipProps={{
                          content: 'Delete widget',
                          position: 'left',
                          showOnDialogEnter: false,
                          hideWhenReferenceHidden: true,
                        }}
                        className="Button--WidgetDashboard"
                      />
                    )}

                    {/* duplicating is a create like any other, so it stops at the same limit */}
                    {isLimitReached ? (
                      <IconButton
                        icon={Duplicate}
                        kind="tertiary"
                        size="small"
                        disabled
                        tooltipProps={{
                          content: LIMIT_REACHED_MESSAGE,
                          position: 'left',
                          showOnDialogEnter: false,
                          hideWhenReferenceHidden: true,
                        }}
                        className="Button--WidgetDashboard"
                      />
                    ) : (
                      <IconButton
                        icon={Duplicate}
                        kind="tertiary"
                        size="small"
                        disabled={isBusy}
                        onClick={() => onDuplicate(widget)}
                        tooltipProps={{
                          content: 'Duplicate widget',
                          position: 'left',
                          showOnDialogEnter: false,
                          hideWhenReferenceHidden: true,
                        }}
                        className="Button--WidgetDashboard"
                      />
                    )}
                  </Flex>

                  <Button
                    kind="tertiary"
                    size="small"
                    disabled={isBusy}
                    onClick={() => {
                      setRenameValue(displayName(widget, boardName));
                      setWidgetToRename(widget);
                    }}
                  >
                    Rename
                  </Button>
                </Flex>
              </Flex>
            </Box>
          ))}
        </Flex>

        <Divider withoutMargin />

        <Box padding="large" backgroundColor="greyBackgroundColor">
          <Text type="text3" align="start" color="secondary" element="p">
            Each widget has its own layout, settings and embed code, and is published on its own.
          </Text>
        </Box>

        {newName !== null ? (
          <AppModal contentSpacing={false} width={300} show onClose={() => setNewName(null)}>
            <AppModalContent>
              <Box marginBottom="large">
                <Heading type="h3" weight="bold" align="start" style={{ marginBottom: '10px' }}>
                  New widget
                </Heading>
                <TextField
                  size="medium"
                  value={newName}
                  placeholder="e.g. Events list"
                  maxLength={100}
                  onChange={(value: string) => setNewName(value)}
                />
              </Box>

              <Box marginBottom="medium">
                <Button
                  disabled={isBusy || !newName.trim()}
                  size="medium"
                  kind="primary"
                  onClick={async () => {
                    const name = newName.trim();

                    if (!name) {
                      return;
                    }

                    setNewName(null);
                    await onCreate(name);
                  }}
                  style={{ width: '100%' }}
                >
                  Create widget
                </Button>
              </Box>
            </AppModalContent>
          </AppModal>
        ) : null}

        {widgetToRename ? (
          <AppModal contentSpacing={false} width={300} show onClose={() => setWidgetToRename(null)}>
            <AppModalContent>
              <Box marginBottom="large">
                <Heading type="h3" weight="bold" align="start" style={{ marginBottom: '10px' }}>
                  Rename widget
                </Heading>
                <TextField
                  size="medium"
                  value={renameValue}
                  placeholder="e.g. Events list"
                  maxLength={100}
                  onChange={(value: string) => setRenameValue(value)}
                />
              </Box>

              <Box marginBottom="medium">
                <Button
                  disabled={isBusy || !renameValue.trim()}
                  size="medium"
                  kind="primary"
                  onClick={async () => {
                    const name = renameValue.trim();
                    const target = widgetToRename;

                    if (!name) {
                      return;
                    }

                    setWidgetToRename(null);
                    await onRename(target, name);
                  }}
                  style={{ width: '100%' }}
                >
                  Save name
                </Button>
              </Box>
            </AppModalContent>
          </AppModal>
        ) : null}

        {widgetToDelete ? (
          <AppModal contentSpacing={false} alertDialog={true} width={300} show onClose={() => setWidgetToDelete(null)}>
            <AppModalContent>
              <Box marginBottom="large">
                <Heading type="h3" weight="bold" align="start" style={{ marginBottom: '10px' }}>
                  Delete widget
                </Heading>
                <Text type="text2" align="start" color="secondary" element="p">
                  "{displayName(widgetToDelete, boardName)}" and all of its settings will be removed permanently. This can't be undone.
                </Text>
              </Box>

              <Box marginBottom="medium">
                <Button
                  disabled={isBusy}
                  size="medium"
                  kind="primary"
                  color="negative"
                  onClick={async () => {
                    const target = widgetToDelete;
                    setWidgetToDelete(null);
                    await onDelete(target.id);
                  }}
                  style={{ width: '100%' }}
                >
                  Delete widget
                </Button>
              </Box>
            </AppModalContent>
          </AppModal>
        ) : null}
      </Box>
    </div>
  );
}
